export type LegislativeControlStatus =
  | 'automated'
  | 'manual_input_required'
  | 'external_submission_required'
  | 'not_supported';

export type LegislativeProgramScope = 'RAC' | 'SAH' | 'DPO' | 'ASSOCIATED_PROVIDER' | 'PLATFORM';

export interface SourceVerificationMeta {
  sourceId: string;
  authority:
    | 'department_health'
    | 'my_aged_care'
    | 'services_australia'
    | 'fair_work'
    | 'aihw'
    | 'dss'
    | 'acqsc';
  title: string;
  verifiedOn: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  sourceUrl?: string;
  notes?: string;
}

export interface ManualObligationStep {
  id: string;
  title: string;
  description: string;
  ownerRole?: string;
  evidenceHint?: string;
  externalSystem?: string;
  required: boolean;
}

export interface LegislativeWorkflowEvidenceRoute {
  id: string;
  title: string;
  route: string;
  evidenceModels: string[];
  uatExpectation: string;
  required: boolean;
  residualBoundary?: string;
}

export interface LegislativeControlRecord {
  controlId: string;
  modulePath: string;
  title: string;
  obligation: string;
  effectiveDateLogic: string;
  status: LegislativeControlStatus;
  dataModels: string[];
  sourceIds: string[];
  programScopes?: LegislativeProgramScope[];
  implementationPhase?: 'NON_SAH_FOUNDATION' | 'SAH_FINAL_PHASE' | string;
  supportAtHomeDeferred?: boolean;
  governmentIntegrationExcluded?: boolean;
  supersededByControlId?: string;
  notes?: string;
  manualSteps?: ManualObligationStep[];
  workflowEvidenceRoutes?: LegislativeWorkflowEvidenceRoute[];
}

export interface ExpandedLegislativeControl extends LegislativeControlRecord {
  sources: SourceVerificationMeta[];
}

export interface LegislativeCompletenessSummary {
  totalControls: number;
  automatedCount: number;
  manualInputRequiredCount: number;
  externalSubmissionRequiredCount: number;
  unsupportedCount: number;
  residualBoundaryCount: number;
  sourceCount: number;
  manualStepCount: number;
  residualControls: ExpandedLegislativeControl[];
}

export const LEGISLATIVE_SOURCES: Record<string, SourceVerificationMeta> = {
  aged_care_act_2024_core: {
    sourceId: 'aged_care_act_2024_core',
    authority: 'department_health',
    title: 'Aged Care Act 2024 No. 104, 2024',
    verifiedOn: '2026-09-21',
    effectiveFrom: '2025-11-01',
    sourceUrl: 'https://www.legislation.gov.au/C2024A00104/latest/text',
    notes:
      'Federal Register latest text, compilation C2026C00301 (Compilation No. 2, compilation date 1 July 2026), registered 16 July 2026 — supersedes the previously pinned C2025C00589.',
  },
  aged_care_rules_2025_current: {
    sourceId: 'aged_care_rules_2025_current',
    authority: 'department_health',
    title: 'Aged Care Rules 2025',
    verifiedOn: '2026-09-21',
    effectiveFrom: '2025-11-01',
    sourceUrl: 'https://www.legislation.gov.au/F2025L01173/latest/text',
    notes:
      'Federal Register latest text for the Rules, compilation F2026C00802 (Compilation No. 10, compilation date 1 August 2026), registered 13 August 2026, which includes the 06 June 2026 Motor Neurone Disease Priority amendment linked to the deferred SAH support-plan/classification control.',
  },
  aged_care_rules_2026_mnd_priority: {
    sourceId: 'aged_care_rules_2026_mnd_priority',
    authority: 'department_health',
    title: 'Aged Care Amendment (Motor Neurone Disease Priority) Rules 2026',
    verifiedOn: '2026-06-13',
    effectiveFrom: '2026-06-06',
    sourceUrl: 'https://www.legislation.gov.au/F2026L00693/latest',
    notes:
      'Amendment listed by the Federal Register as affecting Aged Care Rules 2025 from 6 June 2026; linked to the deferred SAH support-plan/classification control without introducing an active MND workflow in this phase.',
  },
  health_new_act_provider_resources: {
    sourceId: 'health_new_act_provider_resources',
    authority: 'department_health',
    title: 'New Aged Care Act resources for aged care providers',
    verifiedOn: '2026-06-13',
    effectiveFrom: '2025-11-01',
    sourceUrl: 'https://www.health.gov.au/our-work/aged-care-act/resources/providers',
    notes:
      'Department provider hub for registration conditions, obligations, readiness and transition resources under the new Act.',
  },
  acqsc_strengthened_quality_standards: {
    sourceId: 'acqsc_strengthened_quality_standards',
    authority: 'acqsc',
    title: 'Strengthened Aged Care Quality Standards',
    verifiedOn: '2026-06-13',
    effectiveFrom: '2025-11-01',
    sourceUrl:
      'https://www.agedcarequality.gov.au/providers/quality-standards/strengthened-aged-care-quality-standards',
    notes: 'ACQSC source confirming the strengthened Quality Standards apply from 1 November 2025.',
  },
  acqsc_strengthened_provider_guidance: {
    sourceId: 'acqsc_strengthened_provider_guidance',
    authority: 'acqsc',
    title: 'Strengthened Quality Standards - Provider Guidance',
    verifiedOn: '2026-06-13',
    effectiveFrom: '2025-11-01',
    sourceUrl:
      'https://www.agedcarequality.gov.au/providers/strengthened-quality-standards-provider-guidance',
    notes:
      'ACQSC provider guidance for demonstrating conformance with each strengthened Standard outcome.',
  },
  department_qi_program: {
    sourceId: 'department_qi_program',
    authority: 'department_health',
    title: 'National Aged Care Quality Indicator Program (QI Program)',
    verifiedOn: '2026-06-13',
    sourceUrl: 'https://www.health.gov.au/our-work/qi-program?language=en',
    notes:
      'Department source for the residential QI Program, including quarterly reporting against the current quality indicators.',
  },
  aged_care_act_2024_sirs: {
    sourceId: 'aged_care_act_2024_sirs',
    authority: 'department_health',
    title: 'Aged Care Act 2024 s 16 — Meaning of reportable incident',
    verifiedOn: '2026-06-13',
    effectiveFrom: '2025-11-01',
    sourceUrl: 'https://www.legislation.gov.au/C2024A00104/latest/text',
    notes: 'Defines the 8 reportable SIRS incident categories in force from 1 November 2025.',
  },
  acqsc_sirs_guidance: {
    sourceId: 'acqsc_sirs_guidance',
    authority: 'acqsc',
    title: 'ACQSC — About reportable incidents',
    verifiedOn: '2026-06-13',
    effectiveFrom: '2025-11-01',
    sourceUrl:
      'https://www.agedcarequality.gov.au/providers/serious-incident-response-scheme/reportable-incidents/about-reportable-incidents',
    notes:
      'Operational guidance for SIRS reportability, Priority 1/2 triage, and provider response timing.',
  },
  sah_costs_contributions_2025_11: {
    sourceId: 'sah_costs_contributions_2025_11',
    authority: 'my_aged_care',
    title: 'Support at Home costs and contributions',
    verifiedOn: '2026-06-13',
    effectiveFrom: '2025-11-01',
    sourceUrl: 'https://www.myagedcare.gov.au/support-at-home-costs-and-contributions',
    notes:
      'Includes participant contribution settings, AT-HM funding tiers, and carry-over guidance from program commencement.',
  },
  sah_rules_2025_ch4: {
    sourceId: 'sah_rules_2025_ch4',
    authority: 'department_health',
    title: 'Aged Care Rules 2025 Chapter 4 — Support at Home',
    verifiedOn: '2026-06-13',
    effectiveFrom: '2025-11-01',
    sourceUrl: 'https://www.legislation.gov.au/F2025L01173/latest/text',
  },
  sah_statement_guide_2025_10: {
    sourceId: 'sah_statement_guide_2025_10',
    authority: 'department_health',
    title: 'Support at Home monthly statement guide for older people',
    verifiedOn: '2026-04-09',
    effectiveFrom: '2025-10-01',
    sourceUrl:
      'https://www.health.gov.au/sites/default/files/2025-10/monthly-statement-guide-for-older-people.pdf',
  },
  sah_pooled_care_management_2025_09: {
    sourceId: 'sah_pooled_care_management_2025_09',
    authority: 'department_health',
    title:
      'Support at Home pooled care management funding and service delivery branches fact sheet',
    verifiedOn: '2026-04-09',
    effectiveFrom: '2025-11-01',
    sourceUrl:
      'https://www.health.gov.au/sites/default/files/2025-09/support-at-home-program-pooled-care-management-funding-and-service-delivery-branches-fact-sheet_0.pdf',
    notes:
      'Confirms pooled care-management accounts, quarterly crediting, rollover within the financial year, and the service-delivery-branch operating model.',
  },
  residential_schedule_2026_03_20: {
    sourceId: 'residential_schedule_2026_03_20',
    authority: 'department_health',
    title: 'Schedule of fees and charges for residential care from 20 March 2026',
    verifiedOn: '2026-04-09',
    effectiveFrom: '2026-03-20',
    effectiveTo: '2026-09-19',
    sourceUrl:
      'https://www.health.gov.au/sites/default/files/2026-03/schedule_of_fees_and_charges_for_residential_care_-_20_march_2026.pdf',
  },
  residential_schedule_2026_09_20: {
    sourceId: 'residential_schedule_2026_09_20',
    authority: 'department_health',
    title: 'Schedule of fees and charges for residential care from 20 September 2026',
    verifiedOn: '2026-09-30',
    effectiveFrom: '2026-09-20',
    sourceUrl:
      'https://www.health.gov.au/sites/default/files/2026-09/schedule_of_fees_and_charges_for_residential_care_20_september_2026.pdf',
  },
  residential_mpir_2026_q4: {
    sourceId: 'residential_mpir_2026_q4',
    authority: 'department_health',
    title: 'BIR and MPIR for residential aged care — October to December 2026 quarter',
    verifiedOn: '2026-09-30',
    effectiveFrom: '2026-10-01',
    effectiveTo: '2026-12-31',
    sourceUrl:
      'https://www.health.gov.au/sites/default/files/2026-09/base-interest-rate-bir-and-maximum-permissible-interest-rate-mpir-for-residential-aged-care_0.pdf',
    notes: 'MPIR 8.51%, BIR 3.75%',
  },
  residential_mpir_2026_q2: {
    sourceId: 'residential_mpir_2026_q2',
    authority: 'department_health',
    title: 'BIR and MPIR for residential aged care — April to June 2026 quarter',
    verifiedOn: '2026-04-09',
    effectiveFrom: '2026-04-01',
    effectiveTo: '2026-06-30',
    sourceUrl:
      'https://www.health.gov.au/sites/default/files/2025-12/base-interest-rate-bir-and-maximum-permissible-interest-rate-mpir-for-residential-aged-care_0.pdf',
    notes: 'MPIR 7.96%, BIR 3.25%',
  },
  fair_work_schads_2025_10: {
    sourceId: 'fair_work_schads_2025_10',
    authority: 'fair_work',
    title: 'Fair Work SCHADS Award pay guide effective 1 October 2025',
    verifiedOn: '2026-04-09',
    effectiveFrom: '2025-10-01',
    sourceUrl: 'https://www.fairwork.gov.au/pay-and-wages/minimum-wages/pay-guides',
  },
  fair_work_pay_guides_current: {
    sourceId: 'fair_work_pay_guides_current',
    authority: 'fair_work',
    title: 'Fair Work Ombudsman pay guides for modern awards',
    verifiedOn: '2026-06-19',
    sourceUrl: 'https://www.fairwork.gov.au/pay-and-wages/minimum-wages/pay-guides',
    notes:
      'Pay guides provide minimum award pay rates, penalties and allowances; they do not apply where an enterprise agreement covers the employee. Verified before the 1 July 2026 Annual Wage Review transition; Fair Work notes minimum award rates increase from 1 July 2026 and pay guides/tools are under review until updated. Re-verify date-effective pay-guide rows before applying rates for first full pay periods on or after 1 July 2026.',
  },
  aihw_qi_program: {
    sourceId: 'aihw_qi_program',
    authority: 'aihw',
    title: 'National Aged Care Mandatory Quality Indicator Program',
    verifiedOn: '2026-04-09',
    sourceUrl:
      'https://www.aihw.gov.au/reports-data/health-welfare-services/aged-care-quality-indicators/overview',
  },
  dss_dex_bulk_upload: {
    sourceId: 'dss_dex_bulk_upload',
    authority: 'dss',
    title: 'DSS Data Exchange protocols and technical specifications',
    verifiedOn: '2026-06-13',
    sourceUrl: 'https://dex.dss.gov.au/document/131',
    notes:
      'DSS Data Exchange bulk upload technical specification for externally submitted DEX payloads.',
  },
};

export const LEGISLATIVE_STATUS_SUMMARIES: Record<
  LegislativeControlStatus,
  { label: string; summary: string }
> = {
  automated: {
    label: 'Automated',
    summary: 'The platform currently enforces this obligation in-system.',
  },
  manual_input_required: {
    label: 'Manual Input Required',
    summary:
      'The platform supports the workflow, but provider review or manual evidence is still required.',
  },
  external_submission_required: {
    label: 'External Submission Required',
    summary:
      'The platform prepares the underlying data, but the final regulated submission still occurs outside the system.',
  },
  not_supported: {
    label: 'Not Supported',
    summary:
      'The platform does not currently produce a compliant end-to-end output for this obligation.',
  },
};

export const NEW_ACT_CANONICAL_CONTROL_IDS = [
  'provider_registration_conditions',
  'rights_statement_and_supporters',
  'quality_standards_and_continuous_improvement',
  'code_of_conduct_and_worker_training',
  'responsible_person_suitability',
  'worker_screening_ahpra_competency',
  'records_access_privacy_retention',
  'governing_body_advisory_bodies',
  'incident_management_sirs_open_disclosure',
  'complaints_feedback_whistleblower',
  'restrictive_practices_behaviour_support',
  'associated_provider_subcontractor_boundary',
  'digital_platform_operator_obligations',
  'rac_admission_resident_agreements',
  'rac_occupancy_transitions_discharge',
  'rac_care_minutes_rn_coverage_subsidy',
  'clinical_medication_wound_governance',
  'rac_quality_indicators_and_gpms',
  'rac_prudential_accommodation_financial',
  'dex_reporting_external_submission',
  'sah_support_plan_classification_budget',
  'sah_claims_statements_contributions',
  'sah_at_hm_restorative_eol_pathways',
] as const;

export type NewActCanonicalControlId = (typeof NEW_ACT_CANONICAL_CONTROL_IDS)[number];

/**
 * New-Act controls whose obligation attaches to the REGISTERED head provider (it holds the
 * registration / funded package / residential places) and NOT to an "associated provider" that
 * merely delivers care under it — Aged Care Act 2024 s 11(6); ACQSC Regulatory Bulletin
 * RB 2025-01 "Associated providers" (v1.0, 13 Oct 2025).
 *
 * These are hidden from a tenant whose operating_model is 'associated'. Conservative set: every entry
 * attaches unambiguously to the registration / package / place holder and has NO direct-binding slice
 * the associated provider owns. Controls that DO bind the associated provider directly stay VISIBLE
 * and are deliberately excluded here: code_of_conduct_and_worker_training, worker_screening_ahpra_
 * competency, incident_management_sirs_open_disclosure (identification + internal mgmt + report-to-head
 * bind the sub; only the Commission lodgement is the head's), restrictive_practices_behaviour_support
 * (use binds whoever applies it), records_access_privacy_retention, complaints_feedback_whistleblower,
 * rights_statement_and_supporters (worker-level), clinical_medication_wound_governance (safety of care
 * the sub delivers), associated_provider_subcontractor_boundary (the sub's own downstream oversight).
 * UNCERTAIN controls are also excluded pending primary-source confirmation:
 * quality_standards_and_continuous_improvement, responsible_person_suitability (s 12 can attach to the
 * sub's own senior staff), digital_platform_operator_obligations (orthogonal to operating_model).
 *
 * Load-bearing caveat: this classification is valid only while a registered head provider is LINKED to
 * the associated tenant's funded clients (the per-client brokered_out → head-provider gate enforces
 * that). associated_provider_subcontractor_boundary stays visible precisely to surface that linkage.
 */
export const NEW_ACT_REGISTERED_PROVIDER_ONLY_CONTROL_IDS = [
  'provider_registration_conditions',
  'governing_body_advisory_bodies',
  'rac_admission_resident_agreements',
  'rac_occupancy_transitions_discharge',
  'rac_care_minutes_rn_coverage_subsidy',
  'rac_quality_indicators_and_gpms',
  'rac_prudential_accommodation_financial',
  'dex_reporting_external_submission',
  'sah_support_plan_classification_budget',
  'sah_claims_statements_contributions',
  'sah_at_hm_restorative_eol_pathways',
] as const;

/**
 * Whether a new-Act control is visible for a tenant's operating model. An associated provider
 * (operating_model='associated') does not see registered-provider-only obligations; 'registered',
 * 'mixed', or an unknown/null model see everything (fail-open to MORE obligations, never fewer).
 */
export function isControlVisibleForOperatingModel(
  control: Pick<LegislativeControlRecord, 'controlId'>,
  operatingModel: string | null | undefined,
): boolean {
  if (operatingModel !== 'associated') return true;
  return !(NEW_ACT_REGISTERED_PROVIDER_ONLY_CONTROL_IDS as readonly string[]).includes(
    control.controlId,
  );
}

const NEW_ACT_CANONICAL_CONTROL_ID_SET = new Set<string>(NEW_ACT_CANONICAL_CONTROL_IDS);

export const NEW_ACT_WORKFLOW_EVIDENCE_ROUTES: Record<
  NewActCanonicalControlId,
  LegislativeWorkflowEvidenceRoute[]
> = {
  provider_registration_conditions: [
    {
      id: 'provider-registration-record',
      title: 'Provider registration record and conditions',
      route:
        '/compliance/registration?tab=obligations&newActControl=provider_registration_conditions#provider_registration_conditions',
      evidenceModels: ['tenants', 'facilities', 'service_areas', 'responsible_persons'],
      uatExpectation:
        'Reviewer can verify registration category, approved home/facility scope, imposed conditions, and retained external evidence.',
      required: true,
      residualBoundary:
        'Registration decisions and variation notices remain manual external evidence.',
    },
  ],
  rights_statement_and_supporters: [
    {
      id: 'rights-acknowledgement-and-supporter-authority',
      title: 'Rights acknowledgement and supporter authority',
      route:
        '/compliance/provider-obligations?newActControl=rights_statement_and_supporters#rights_statement_and_supporters',
      evidenceModels: [
        'statement_of_rights_acknowledgements',
        'client_contacts',
        'signature_blocks',
        'consents',
      ],
      uatExpectation:
        'Reviewer can record or inspect Statement of Rights acknowledgement evidence and attach authority/supporter notes.',
      required: true,
      residualBoundary:
        'This control uses provider-obligations as both dashboard and evidence route because the Statement of Rights acknowledgement forms live on this page; provider explanation, interpreter support, and authority judgement remain manual evidence.',
    },
  ],
  quality_standards_and_continuous_improvement: [
    {
      id: 'quality-standards-and-improvement-pack',
      title: 'Standards evidence and improvement pack',
      route:
        '/compliance/quality/hub?newActControl=quality_standards_and_continuous_improvement#quality-standards-evidence',
      evidenceModels: [
        'quality_standards',
        'quality_assessments',
        'continuous_improvement_items',
        'complaints',
        'governance_attestations',
      ],
      uatExpectation:
        'Reviewer can open the quality hub and trace standards evidence into continuous-improvement actions.',
      required: true,
      residualBoundary:
        'Commission audit interpretation and final regulatory outcomes remain manual evidence.',
    },
    {
      id: 'continuous-improvement-register',
      title: 'Continuous improvement register',
      route:
        '/compliance/continuous-improvement?newActControl=quality_standards_and_continuous_improvement#continuous-improvement-register',
      evidenceModels: ['continuous_improvement_items'],
      uatExpectation:
        'Reviewer can verify open, overdue, and completed improvement items tied to quality evidence.',
      required: true,
    },
  ],
  code_of_conduct_and_worker_training: [
    {
      id: 'conduct-acknowledgement-and-training',
      title: 'Conduct acknowledgement and training evidence',
      route:
        '/people/policies-conduct/code?newActControl=code_of_conduct_and_worker_training#conduct-training-evidence',
      evidenceModels: [
        'conduct_obligations',
        'conduct_acknowledgments',
        'conduct_breaches',
        'lms_training_assignments',
      ],
      uatExpectation:
        'Reviewer can inspect conduct acknowledgements, allegations, investigations, and training completion evidence.',
      required: true,
      residualBoundary:
        'External course validity and provider waiver decisions remain manual evidence.',
    },
  ],
  responsible_person_suitability: [
    {
      id: 'responsible-person-register',
      title: 'Responsible person suitability register',
      route:
        '/compliance/responsible-persons?newActControl=responsible_person_suitability#responsible-person-register',
      evidenceModels: ['responsible_persons', 'responsible_person_changes', 'audit_logs'],
      uatExpectation:
        'Reviewer can inspect responsible person suitability, appointment/cessation, and change evidence.',
      required: true,
      residualBoundary: 'External regulator notification receipt remains manual evidence.',
    },
  ],
  worker_screening_ahpra_competency: [
    {
      id: 'worker-screening-register',
      title: 'Worker screening and AHPRA evidence',
      route:
        '/people/compliance/screening?newActControl=worker_screening_ahpra_competency#worker-screening-register',
      evidenceModels: [
        'worker_screenings',
        'staff',
        'worker_certifications',
        'staff_classification_history',
      ],
      uatExpectation:
        'Reviewer can inspect screening status, expiry, conditions, registration evidence, and role capability blocks.',
      required: true,
      residualBoundary: 'AHPRA and worker-screening source checks remain manual until integrated.',
    },
    {
      id: 'worker-certification-register',
      title: 'Worker certification register',
      route:
        '/people/compliance/certifications?newActControl=worker_screening_ahpra_competency#worker-certification-register',
      evidenceModels: ['worker_certifications'],
      uatExpectation:
        'Reviewer can verify active, expired, and renewal-required certification evidence.',
      required: true,
    },
  ],
  records_access_privacy_retention: [
    {
      id: 'audit-and-retention-spine',
      title: 'Audit, document, and retention spine',
      route:
        '/compliance/audit-trail?newActControl=records_access_privacy_retention#audit-retention-spine',
      evidenceModels: [
        'audit_logs',
        'regulated_workflow_evidence',
        'documents',
        'document_links',
        'portal_access_logs',
      ],
      uatExpectation:
        'Reviewer can inspect audit/evidence records and verify retention/access evidence is available.',
      required: true,
      residualBoundary:
        'Provider privacy notices and external repositories remain operational controls.',
    },
    {
      id: 'audit-pack-export',
      title: 'Audit pack export',
      route:
        '/compliance/audit-pack/export?newActControl=records_access_privacy_retention#audit-pack-export',
      evidenceModels: ['regulated_workflow_evidence', 'documents'],
      uatExpectation:
        'Reviewer can prepare an evidence pack for regulated review without exposing PHI to unsupported channels.',
      required: true,
    },
  ],
  governing_body_advisory_bodies: [
    {
      id: 'governance-and-advisory-body-evidence',
      title: 'Governance and advisory-body evidence',
      route:
        '/compliance/governance?newActControl=governing_body_advisory_bodies#governance-advisory-body-evidence',
      evidenceModels: [
        'consumer_advisory_bodies',
        'governance_attestations',
        'board_meetings',
        'board_meeting_action_items',
        'responsible_persons',
      ],
      uatExpectation:
        'Reviewer can inspect advisory-body cadence, governance attestations, and board action evidence.',
      required: true,
      residualBoundary:
        'Board composition judgement and accepted report sign-off remain manual evidence.',
    },
  ],
  incident_management_sirs_open_disclosure: [
    {
      id: 'incident-sirs-open-disclosure-workflow',
      title: 'Incident, SIRS, and open-disclosure workflow',
      route:
        '/compliance/incidents?newActControl=incident_management_sirs_open_disclosure#incident-sirs-open-disclosure-workflow',
      evidenceModels: [
        'incidents',
        'incident_audit_events',
        'open_disclosure_steps',
        'workflow_tasks',
        'regulated_workflow_evidence',
      ],
      uatExpectation:
        'Reviewer can inspect reportability, priority timing, workflow tasks, open-disclosure steps, and external reference evidence.',
      required: true,
      residualBoundary:
        'Final Commission portal lodgement and acknowledgement remain manual external evidence.',
    },
  ],
  complaints_feedback_whistleblower: [
    {
      id: 'complaints-and-feedback-workflow',
      title: 'Complaints and feedback workflow',
      route:
        '/compliance/complaints?newActControl=complaints_feedback_whistleblower#complaints-feedback-workflow',
      evidenceModels: [
        'complaints',
        'complaint_communications',
        'complaint_improvements',
        'feedback',
      ],
      uatExpectation:
        'Reviewer can inspect acknowledgement, investigation, communication, resolution, and improvement evidence.',
      required: true,
      residualBoundary: 'External referral and regulator-contact judgement remain manual evidence.',
    },
    {
      id: 'whistleblower-workflow',
      title: 'Whistleblower workflow',
      route:
        '/compliance/whistleblower/recipients?newActControl=complaints_feedback_whistleblower#whistleblower-workflow',
      evidenceModels: ['whistleblower_recipients', 'whistleblower_reports'],
      uatExpectation:
        'Reviewer can inspect protected reporting recipient setup and protected-report workflow routing without opening confidential disclosures.',
      required: true,
    },
  ],
  restrictive_practices_behaviour_support: [
    {
      id: 'restrictive-practice-register',
      title: 'Restrictive practice register',
      route:
        '/compliance/restrictive-practices?newActControl=restrictive_practices_behaviour_support#restrictive-practice-register',
      evidenceModels: [
        'restrictive_practices',
        'restrictive_practice_episodes',
        'restrictive_practice_reviews',
        'care_plans',
        'incidents',
      ],
      uatExpectation:
        'Reviewer can inspect consent, BSP, episode, emergency-use, review, and SIRS side-effect evidence.',
      required: true,
      residualBoundary:
        'Substitute decision-maker authority and external report evidence remain manual controls.',
    },
  ],
  associated_provider_subcontractor_boundary: [
    {
      id: 'contractor-associated-provider-evidence',
      title: 'Associated provider and contractor evidence',
      route:
        '/contractors?newActControl=associated_provider_subcontractor_boundary#associated-provider-oversight',
      evidenceModels: ['contractors', 'contractor_documents', 'contractor_audit_events'],
      uatExpectation:
        'Reviewer can inspect contractor scope, documents, audit events, and service boundary evidence.',
      required: true,
      residualBoundary:
        'Provider oversight judgement and third-party contract interpretation remain manual evidence.',
    },
    {
      id: 'contractor-evidence-queue',
      title: 'Contractor evidence queue',
      route:
        '/contractors/evidence/queue?newActControl=associated_provider_subcontractor_boundary#contractor-evidence-queue',
      evidenceModels: ['contractor_documents', 'contractor_audit_events'],
      uatExpectation:
        'Reviewer can verify evidence queue items that still need provider oversight.',
      required: true,
    },
  ],
  digital_platform_operator_obligations: [
    {
      id: 'platform-operator-applicability-review',
      title: 'Digital platform operator applicability review',
      route:
        '/settings/system?newActControl=digital_platform_operator_obligations#digital-platform-operator-boundary',
      evidenceModels: ['tenants', 'contractors', 'worker_screenings', 'complaints', 'incidents'],
      uatExpectation:
        'Reviewer can inspect platform settings and retain the legal/applicability assessment for DPO scope.',
      required: true,
      residualBoundary: 'DPO applicability and notices remain manual legal/regulator evidence.',
    },
  ],
  rac_admission_resident_agreements: [
    {
      id: 'rac-admission-intake-evidence',
      title: 'RAC admission and resident agreement evidence',
      route:
        '/residential/admissions?newActControl=rac_admission_resident_agreements#rac-admission-intake-evidence',
      evidenceModels: [
        'residential_admission_intakes',
        'residential_admission_intake_items',
        'rac_resident_opening_states',
        'signature_blocks',
        'documents',
      ],
      uatExpectation:
        'Reviewer can verify admission checklist, representative authority, signatures, agreements, and opening-state gates.',
      required: true,
      residualBoundary: 'Source document legal review remains manual evidence.',
    },
  ],
  rac_occupancy_transitions_discharge: [
    {
      id: 'rac-occupancy-transition-ledger',
      title: 'RAC occupancy, transition, and discharge ledger',
      route:
        '/residential/occupancy?newActControl=rac_occupancy_transitions_discharge#rac-occupancy-transition-ledger',
      evidenceModels: [
        'residential_occupancy_events',
        'residential_bed_status_events',
        'client_transitions',
        'resident_absence_events',
        'residential_respite_episodes',
        'documents',
      ],
      uatExpectation:
        'Reviewer can verify movement, leave, respite, transfer, discharge, death, and source-document evidence.',
      required: true,
      residualBoundary:
        'External transfer/discharge/death source verification remains manual evidence.',
    },
  ],
  rac_care_minutes_rn_coverage_subsidy: [
    {
      id: 'care-minutes-rn-coverage-pack',
      title: 'Care minutes and RN coverage pack',
      route:
        '/compliance/rn-coverage?newActControl=rac_care_minutes_rn_coverage_subsidy#care-minutes-rn-coverage-pack',
      evidenceModels: [
        'res_anacc_classifications',
        'care_minute_targets',
        'daily_care_minute_records',
        'direct_care_attributions',
        'roster_shift_assignments',
        'worker_screenings',
      ],
      uatExpectation:
        'Reviewer can inspect target, delivered care-minute, RN coverage, AN-ACC, and roster evidence.',
      required: true,
      residualBoundary: 'External subsidy/reporting acknowledgement remains manual evidence.',
    },
    {
      id: 'residential-care-minutes-source',
      title: 'Residential care-minute source records',
      route:
        '/residential/care-minutes?newActControl=rac_care_minutes_rn_coverage_subsidy#residential-care-minutes-source',
      evidenceModels: ['care_minute_targets', 'daily_care_minute_records'],
      uatExpectation:
        'Reviewer can trace care-minute records from residential operations into compliance review.',
      required: true,
    },
  ],
  clinical_medication_wound_governance: [
    {
      id: 'clinical-governance-hub',
      title: 'Clinical governance hub',
      route:
        '/compliance/clinical-governance?newActControl=clinical_medication_wound_governance#clinical-governance-hub',
      evidenceModels: [
        'medications',
        'medication_administrations',
        'controlled_drug_register',
        'wounds',
        'wound_assessments',
        'observations',
        'care_plans',
        'clinical_alerts',
        'ipc_infections',
      ],
      uatExpectation:
        'Reviewer can inspect medication, S8, wound, observation, deterioration, IPC, and care-plan follow-through evidence.',
      required: true,
      residualBoundary:
        'Clinical judgement and external clinical source records remain manual evidence.',
    },
  ],
  rac_quality_indicators_and_gpms: [
    {
      id: 'rac-qi-gpms-pack',
      title: 'RAC quality indicators and GPMS pack',
      route: '/compliance/quality?newActControl=rac_quality_indicators_and_gpms#rac-qi-gpms-pack',
      evidenceModels: ['quality_indicator_submissions', 'quality_indicator_records', 'incidents'],
      uatExpectation:
        'Reviewer can inspect QI quarter records, source indicators, validation state, and GPMS-ready evidence.',
      required: true,
      residualBoundary:
        'Final GPMS lodgement, rejection, and acknowledgement remain manual external evidence.',
    },
    {
      id: 'rac-qi-data-entry',
      title: 'QI data entry',
      route:
        '/compliance/quality/qi-data-entry?newActControl=rac_quality_indicators_and_gpms#rac-qi-data-entry',
      evidenceModels: ['quality_indicator_records'],
      uatExpectation: 'Reviewer can verify indicator source values before external submission.',
      required: true,
    },
  ],
  dex_reporting_external_submission: [
    {
      id: 'dex-bulk-upload-pack',
      title: 'DEX bulk-upload pack',
      route:
        '/compliance/reporting/dex?newActControl=dex_reporting_external_submission#dex-bulk-upload-pack',
      evidenceModels: [
        'clients',
        'facilities',
        'services_delivered',
        'dex_outlet_configs',
        'dex_client_profiles',
        'dex_case_profiles',
        'dex_service_mappings',
        'dex_session_overrides',
        'dex_assessment_records',
      ],
      uatExpectation:
        'Reviewer can generate or inspect DEX case/session/outcome payload evidence before manual DSS upload.',
      required: true,
      residualBoundary:
        'DSS upload, acceptance, and rejection handling remain manual external evidence.',
    },
  ],
  rac_prudential_accommodation_financial: [
    {
      id: 'prudential-accommodation-pack',
      title: 'Prudential and accommodation pack',
      route:
        '/compliance/prudential?newActControl=rac_prudential_accommodation_financial#prudential-accommodation-pack',
      evidenceModels: [
        'accommodation_deposits',
        'prudential_refund_events',
        'prudential_refund_deductions',
        'prudential_compliance_statements',
        'prudential_external_submissions',
        'residential_room_prices',
      ],
      uatExpectation:
        'Reviewer can inspect RAD/DAP, refund, disclosure, room price, liquidity, statement, and external filing evidence.',
      required: true,
      residualBoundary:
        'External filings, regulator acknowledgements, and board records remain manual evidence.',
    },
    {
      id: 'prudential-deposit-register',
      title: 'Accommodation deposit register',
      route:
        '/compliance/prudential/deposits?newActControl=rac_prudential_accommodation_financial#prudential-deposit-register',
      evidenceModels: ['accommodation_deposits', 'prudential_refund_events'],
      uatExpectation:
        'Reviewer can trace accommodation deposits and refunds into prudential evidence.',
      required: true,
    },
  ],
  sah_support_plan_classification_budget: [
    {
      id: 'sah-readiness-support-plan-budget',
      title: 'SAH readiness, support-plan, and budget register',
      route:
        '/home-care/readiness?newActControl=sah_support_plan_classification_budget#sah-readiness-register',
      evidenceModels: [
        'care_plans',
        'care_plan_service_inclusions',
        'sah_classifications',
        'sah_quarter_budgets',
        'sah_review_requests',
      ],
      uatExpectation:
        'Reviewer can verify support-plan acceptance, funding and claims readiness, classification, current-quarter budget, and review-request follow-up evidence without treating setup as a care barrier.',
      required: true,
      residualBoundary:
        'External assessor decisions, priority-category source confirmation, and provider support-plan judgement remain manual evidence.',
    },
    {
      id: 'sah-classification-contribution-capture',
      title: 'SAH classification and contribution capture',
      route:
        '/home-care/classifications?newActControl=sah_support_plan_classification_budget#sah-classification-contribution',
      evidenceModels: ['clients', 'sah_classifications', 'sah_means_tier_mappings'],
      uatExpectation:
        'Reviewer can inspect SAH classification level, transitioned-HCP context, contribution tier, assessed rates, and no-worse-off status.',
      required: true,
    },
    {
      id: 'sah-budget-drawdown-proof',
      title: 'SAH quarter budget and drawdown proof',
      route:
        '/home-care/budgets?newActControl=sah_support_plan_classification_budget#sah-budget-drawdown',
      evidenceModels: [
        'sah_quarter_budgets',
        'sah_client_budgets',
        'services_delivered',
        'sah_service_details',
      ],
      uatExpectation:
        'Reviewer can trace quarter allocation, carry-over, care-management reserve, delivered spend, remaining balance, and claim-readiness blockers.',
      required: true,
    },
  ],
  sah_claims_statements_contributions: [
    {
      id: 'sah-claims-external-submission-pack',
      title: 'SAH claim batch and external submission pack',
      route:
        '/home-care/claims?newActControl=sah_claims_statements_contributions#sah-claims-submission-pack',
      evidenceModels: [
        'services_delivered',
        'sah_service_details',
        'claims',
        'sah_claim_batches',
        'sah_claim_batch_submissions',
      ],
      uatExpectation:
        'Reviewer can inspect claim readiness, generated batches, row-level claim lifecycle, submission references, rejection notes, payment references, and reconciliation evidence.',
      required: true,
      residualBoundary:
        'Services Australia provider-portal lodgement, acknowledgement, rejection, and payment confirmation remain manual external evidence until an approved integration is live.',
    },
    {
      id: 'sah-statement-contribution-pack',
      title: 'SAH statements and contribution invoice pack',
      route:
        '/home-care/statements?newActControl=sah_claims_statements_contributions#sah-statement-contribution-pack',
      evidenceModels: [
        'sah_monthly_statements',
        'sah_contribution_events',
        'finance_invoices',
        'finance_invoice_lines',
      ],
      uatExpectation:
        'Reviewer can verify statement readiness, delivery state, recipient continuity, contribution totals, and draft contribution-invoice generation.',
      required: true,
    },
  ],
  sah_at_hm_restorative_eol_pathways: [
    {
      id: 'sah-at-hm-pathway-register',
      title: 'SAH AT-HM approval and installation register',
      route:
        '/home-care/at-hm?newActControl=sah_at_hm_restorative_eol_pathways#sah-at-hm-pathway-register',
      evidenceModels: ['sah_at_hm_requests', 'sah_at_hm_request_evidence', 'documents'],
      uatExpectation:
        'Reviewer can inspect AT-HM approval, estimate, supplier, order, installation, completion, evidence, and budget/claim effect.',
      required: true,
      residualBoundary:
        'External AT-HM assessor/provider approval and supplier-source records remain manual evidence.',
    },
    {
      id: 'sah-restorative-eol-pathway-proof',
      title: 'SAH restorative and end-of-life pathway proof',
      route:
        '/clients/[id]/?tab=sah-hub&newActControl=sah_at_hm_restorative_eol_pathways#restorative-care',
      evidenceModels: [
        'restorative_care_episodes',
        'clinical_pathway_enrolments',
        'workflow_tasks',
        'regulated_workflow_evidence',
      ],
      uatExpectation:
        'Reviewer can verify restorative-care episode limits, lead assignment, completion evidence, and end-of-life pathway follow-through from the client record.',
      required: true,
    },
  ],
};

export const LEGACY_LEGISLATIVE_CONTROL_ALIASES: Record<string, NewActCanonicalControlId> = {
  sah_claims: 'sah_claims_statements_contributions',
  sah_monthly_statements: 'sah_claims_statements_contributions',
  sah_at_hm: 'sah_at_hm_restorative_eol_pathways',
  quality_indicators: 'rac_quality_indicators_and_gpms',
  gpms_preparation: 'rac_quality_indicators_and_gpms',
  dex_reporting: 'dex_reporting_external_submission',
  restrictive_practices: 'restrictive_practices_behaviour_support',
  sirs_incidents: 'incident_management_sirs_open_disclosure',
  prudential_deposits: 'rac_prudential_accommodation_financial',
  prudential_annual_statements: 'rac_prudential_accommodation_financial',
  prudential_accommodation_pricing: 'rac_prudential_accommodation_financial',
  prudential_governance: 'rac_prudential_accommodation_financial',
};

export const LEGACY_LEGISLATIVE_MODULE_PATH_ALIASES: Record<string, NewActCanonicalControlId> = {
  // Pre-convergence module paths retired by the people/workforce convergence
  // (a366cdf50) and the compliance audit route split (7887cd5e9).
  '/compliance/code-of-conduct': 'code_of_conduct_and_worker_training',
  '/compliance/screening': 'worker_screening_ahpra_competency',
  '/compliance/audit': 'records_access_privacy_retention',
  '/home-care/claims': 'sah_claims_statements_contributions',
  '/home-care/statements': 'sah_claims_statements_contributions',
  '/home-care/at-hm': 'sah_at_hm_restorative_eol_pathways',
  '/compliance/reporting/dex': 'dex_reporting_external_submission',
  '/compliance/quality': 'rac_quality_indicators_and_gpms',
  '/compliance/prudential/deposits': 'rac_prudential_accommodation_financial',
  '/compliance/prudential/reports': 'rac_prudential_accommodation_financial',
  '/compliance/prudential/pricing': 'rac_prudential_accommodation_financial',
  '/compliance/prudential/governance': 'rac_prudential_accommodation_financial',
};

export const LEGISLATIVE_CONTROL_MATRIX: LegislativeControlRecord[] = [
  {
    controlId: 'provider_registration_conditions',
    modulePath: '/compliance/registration',
    title: 'Provider registration and conditions',
    obligation:
      'Maintain provider registration, approved residential home status, registration-category scope, conditions, and change-notification evidence.',
    effectiveDateLogic:
      'Registration and conditions apply from 1 November 2025 and are provider, category, service, facility, and service-delivery-branch specific.',
    programScopes: ['RAC', 'SAH'],
    status: 'manual_input_required',
    dataModels: ['tenants', 'facilities', 'service_areas', 'responsible_persons'],
    sourceIds: [
      'aged_care_act_2024_core',
      'aged_care_rules_2025_current',
      'health_new_act_provider_resources',
    ],
    notes:
      'The platform stores provider, facility, branch, and responsible-person evidence, but registration variations and regulator decisions remain provider-controlled external facts until an official integration is introduced.',
    manualSteps: [
      {
        id: 'provider-registration-refresh',
        title: 'Refresh registration evidence',
        description:
          'Confirm the provider registration category, approved residential home status, imposed conditions, and branch/facility scope against the current regulator record before relying on a compliance-ready claim.',
        ownerRole: 'Tenant Admin',
        externalSystem: 'ACQSC provider registration record',
        evidenceHint:
          'Attach the current registration certificate, regulator correspondence, or portal reference to the provider registration record.',
        required: true,
      },
    ],
  },
  {
    controlId: 'rights_statement_and_supporters',
    modulePath: '/compliance/provider-obligations',
    title: 'Statement of Rights, representatives, and supporters',
    obligation:
      'Give, explain, and evidence Statement of Rights information and ensure supporters, representatives, advocates, and authorised contacts are respected without weakening supported decision-making.',
    effectiveDateLogic:
      'Rights, supporter, information, and access obligations apply from 1 November 2025 across RAC and SAH service delivery.',
    programScopes: ['RAC', 'SAH'],
    status: 'manual_input_required',
    dataModels: [
      'statement_of_rights_acknowledgements',
      'client_contacts',
      'signature_blocks',
      'consents',
      'portal_access_logs',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'Acknowledgement, representative authority, signature, and portal-access evidence exist in-platform. Providers still need to verify authority documents and explain rights in a way the person can understand.',
    manualSteps: [
      {
        id: 'rights-explained-and-understood',
        title: 'Confirm rights explanation and authority',
        description:
          'Record how the Statement of Rights was provided or explained and confirm any supporter, representative, guardian, advocate, or interpreter authority before acting on their instructions.',
        ownerRole: 'Coordinator',
        evidenceHint:
          'Keep the signed acknowledgement, verbal-witness note, interpreter note, or authority instrument link.',
        required: true,
      },
    ],
  },
  {
    controlId: 'quality_standards_and_continuous_improvement',
    modulePath: '/compliance/quality/hub',
    title: 'Strengthened Quality Standards and continuous improvement',
    obligation:
      'Operate quality-governance, assessment, consumer engagement, continuous-improvement, and evidence review workflows aligned to the Strengthened Aged Care Quality Standards.',
    effectiveDateLogic:
      'The strengthened Standards apply from 1 November 2025, with Standard 7 scoped to residential community contexts.',
    programScopes: ['RAC', 'SAH'],
    status: 'manual_input_required',
    dataModels: [
      'quality_standards',
      'quality_assessments',
      'continuous_improvement_items',
      'complaints',
      'governance_attestations',
    ],
    sourceIds: [
      'aged_care_rules_2025_current',
      'acqsc_strengthened_quality_standards',
      'acqsc_strengthened_provider_guidance',
    ],
    notes:
      'The platform tracks standards review and improvement evidence. Provider judgement, audit interpretation, and final Commission audit outcomes remain manual evidence until official regulator data is available.',
    manualSteps: [
      {
        id: 'quality-standard-evidence-review',
        title: 'Review evidence against the applicable Standard outcomes',
        description:
          'Confirm the evidence pack demonstrates the applicable outcomes for the provider type and service context before treating a Standard as audit-ready.',
        ownerRole: 'Quality Officer',
        evidenceHint: 'Attach audit notes, self-assessment records, or Commission correspondence.',
        required: true,
      },
    ],
  },
  {
    controlId: 'code_of_conduct_and_worker_training',
    modulePath: '/people/policies-conduct',
    title: 'Code of Conduct and worker training',
    obligation:
      'Evidence Code of Conduct obligations, worker acknowledgement, allegations, breach response, and required training or competency pathways for aged care workers and responsible persons.',
    effectiveDateLogic:
      'Code and worker obligations apply from 1 November 2025 and continue for active workers, volunteers, associated-provider workers, and responsible persons.',
    programScopes: ['RAC', 'SAH'],
    status: 'manual_input_required',
    dataModels: [
      'conduct_obligations',
      'conduct_acknowledgments',
      'conduct_breaches',
      'lms_training_assignments',
      'worker_certifications',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'Statura stores conduct, LMS, certification, and worker evidence. External training authorities and provider policy decisions must still be checked before a worker is treated as fully cleared.',
    manualSteps: [
      {
        id: 'conduct-training-source-check',
        title: 'Validate training and conduct evidence',
        description:
          'Confirm external training certificates, conduct declarations, and breach outcomes against provider policy and current worker duties.',
        ownerRole: 'HR Director',
        evidenceHint: 'Keep the certificate, declaration, breach outcome, or waiver rationale.',
        required: true,
      },
    ],
  },
  {
    controlId: 'responsible_person_suitability',
    modulePath: '/compliance/responsible-persons',
    title: 'Responsible person suitability and notifications',
    obligation:
      'Assess responsible person suitability, maintain suitability evidence, and record appointment, cessation, and change-in-circumstance notifications.',
    effectiveDateLogic:
      'Responsible person suitability is event-driven and must be refreshed when role, status, or suitability matters change.',
    programScopes: ['RAC', 'SAH'],
    status: 'manual_input_required',
    dataModels: ['responsible_persons', 'responsible_person_changes', 'audit_logs'],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'Controlled create/update paths exist for responsible person records. External regulator notification and source checks remain manual evidence.',
    manualSteps: [
      {
        id: 'responsible-person-external-notification',
        title: 'Record regulator notification evidence',
        description:
          'For appointments, cessations, or suitability changes, retain the regulator notification reference or evidence explaining why notification was not required.',
        ownerRole: 'Tenant Admin',
        externalSystem: 'ACQSC provider portal or correspondence',
        evidenceHint: 'Store the notification reference, portal receipt, email, or board minute.',
        required: true,
      },
    ],
  },
  {
    controlId: 'worker_screening_ahpra_competency',
    modulePath: '/people/compliance',
    title: 'Worker screening, AHPRA, vaccination, and competency',
    obligation:
      'Maintain worker screening, responsible-person screening, AHPRA/manual professional registration evidence, vaccination records where applicable, and capability blocks at roster and service boundaries.',
    effectiveDateLogic:
      'Screening and competency obligations are worker, role, registration-category, and expiry-date specific.',
    programScopes: ['RAC', 'SAH'],
    status: 'manual_input_required',
    dataModels: [
      'worker_screenings',
      'staff',
      'worker_certifications',
      'staff_classification_history',
      'roster_shift_assignments',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'Manual AHPRA and screening evidence is supported, with expiry and roster gating. Automated external screening/AHPRA verification is not implemented.',
    manualSteps: [
      {
        id: 'worker-screening-manual-verification',
        title: 'Verify screening and registration externally',
        description:
          'Check police, NDIS, AHPRA, vaccination, or provider-mandated credential evidence before clearing the worker for a regulated role.',
        ownerRole: 'HR Director',
        externalSystem: 'AHPRA / screening provider / state or territory worker screening channel',
        evidenceHint:
          'Attach the certificate, registration lookup evidence, expiry, and conditions.',
        required: true,
      },
    ],
  },
  {
    controlId: 'records_access_privacy_retention',
    modulePath: '/compliance/audit-trail',
    title: 'Records, access, privacy, and retention',
    obligation:
      'Keep required RAC and SAH records, protect personal information, support access by individuals and authorised supporters, and retain compliance evidence for the required period.',
    effectiveDateLogic:
      'Recordkeeping, access, privacy, and retention obligations apply continuously to current and historical service records.',
    programScopes: ['RAC', 'SAH'],
    status: 'automated',
    dataModels: [
      'audit_logs',
      'regulated_workflow_evidence',
      'documents',
      'document_links',
      'portal_access_logs',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'Append-only workflow evidence, audit logs, document authorization, and portal access logs provide the in-platform retention spine. Provider privacy notices, access-decision judgement, and external record repositories remain operational controls.',
  },
  {
    controlId: 'governing_body_advisory_bodies',
    modulePath: '/compliance/governance',
    title: 'Governing body and advisory body obligations',
    obligation:
      'Maintain governing body membership evidence, clinical-care experience where required, quality care advisory body evidence, consumer advisory body evidence, meeting cadence, and escalation reports.',
    effectiveDateLogic:
      'Governance and advisory-body obligations are provider-type, registration-category, and service-context specific.',
    programScopes: ['RAC', 'SAH'],
    status: 'manual_input_required',
    dataModels: [
      'consumer_advisory_bodies',
      'governance_attestations',
      'board_meetings',
      'board_meeting_action_items',
      'responsible_persons',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'CAB cadence and governance artefacts are represented. Provider board composition, advisory-body membership, and report acceptance still require manual governance sign-off.',
    manualSteps: [
      {
        id: 'governance-body-review',
        title: 'Confirm body membership and reports',
        description:
          'Confirm advisory body membership, meeting evidence, reports, and governing-body composition against the provider obligations that apply to the provider type.',
        ownerRole: 'Governance Lead',
        evidenceHint:
          'Keep agenda, minutes, attendance, report, escalation, and board action links.',
        required: true,
      },
    ],
  },
  {
    controlId: 'incident_management_sirs_open_disclosure',
    modulePath: '/compliance/incidents',
    title: 'Incident management, SIRS, and open disclosure',
    obligation:
      'Operate an incident management system, identify reportable incidents, complete open disclosure, track priority deadlines, and evidence Commission reporting where required.',
    effectiveDateLogic:
      'Incident obligations are triggered by awareness/discovery time, incident category, harm, reportability, and any Commission follow-up request.',
    programScopes: ['RAC', 'SAH'],
    status: 'manual_input_required',
    dataModels: [
      'incidents',
      'incident_audit_events',
      'open_disclosure_steps',
      'workflow_tasks',
      'regulated_workflow_evidence',
    ],
    sourceIds: [
      'aged_care_act_2024_core',
      'aged_care_act_2024_sirs',
      'aged_care_rules_2025_current',
      'acqsc_sirs_guidance',
    ],
    notes:
      'The platform derives reportability and workflow timing, but final category judgement and Commission portal lodgement/acknowledgement remain manual evidence unless integrated.',
    manualSteps: [
      {
        id: 'sirs-external-lodgement-evidence',
        title: 'Record SIRS lodgement and acknowledgement',
        description:
          'When the incident is reportable, record the Commission portal reference, acknowledgement, final-report request, or reason a report was not required.',
        ownerRole: 'Quality Officer',
        externalSystem: 'ACQSC SIRS portal',
        evidenceHint:
          'Keep the portal receipt, acknowledgement reference, or reportability rationale.',
        required: true,
      },
    ],
  },
  {
    controlId: 'complaints_feedback_whistleblower',
    modulePath: '/compliance/complaints',
    title: 'Complaints, feedback, and whistleblower systems',
    obligation:
      'Maintain complaints, feedback, whistleblower, acknowledgement, investigation, resolution, improvement, communication, and escalation evidence.',
    effectiveDateLogic:
      'Complaint and whistleblower obligations are event-driven and apply to provider and, where relevant, digital-platform workflows.',
    programScopes: ['RAC', 'SAH'],
    status: 'automated',
    dataModels: [
      'complaints',
      'complaint_communications',
      'complaint_improvements',
      'feedback',
      'whistleblower_recipients',
      'whistleblower_reports',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'In-platform intake, communications, resolution, and governance closeout exist. Providers still need to use judgement for external referrals, whistleblower protection steps, and regulator contact where required.',
  },
  {
    controlId: 'restrictive_practices_behaviour_support',
    modulePath: '/compliance/restrictive-practices',
    title: 'Restrictive practices and behaviour support',
    obligation:
      'Ensure restrictive practices are last resort, authorised, consented where required, tied to behaviour support evidence, reviewed, and cross-trigger SIRS where inappropriate use is suspected.',
    effectiveDateLogic:
      'Restrictive-practice controls are person, episode, consent, emergency-use, practice-type, and review-cadence specific across RAC and any SAH context where restrictive-practice obligations apply.',
    programScopes: ['RAC', 'SAH'],
    status: 'manual_input_required',
    dataModels: [
      'restrictive_practices',
      'restrictive_practice_episodes',
      'restrictive_practice_reviews',
      'care_plans',
      'incidents',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'Workflow and review evidence are tracked. Substitute decision-maker authority, clinical judgement, and any external report remain manual evidence.',
    manualSteps: [
      {
        id: 'rp-consent-bsp-review',
        title: 'Validate consent and behaviour support evidence',
        description:
          'Confirm the current consent authority, less-restrictive alternatives, behaviour support plan, emergency-use rationale, and review record before relying on the restrictive-practice register.',
        ownerRole: 'Clinical Lead',
        evidenceHint: 'Keep consent, BSP, emergency-use note, review, and care-plan links.',
        required: true,
      },
    ],
  },
  {
    controlId: 'associated_provider_subcontractor_boundary',
    modulePath: '/contractors',
    title: 'Associated provider and subcontractor boundary',
    obligation:
      'Track associated providers and subcontractors delivering services on behalf of a registered provider, including scope, worker obligations, incident/complaint routing, evidence access, and provider oversight.',
    effectiveDateLogic:
      'Associated-provider obligations apply when a third party represents or delivers funded aged care services for the registered provider.',
    programScopes: ['RAC', 'SAH', 'ASSOCIATED_PROVIDER'],
    status: 'manual_input_required',
    dataModels: ['contractors', 'contractor_documents', 'contractor_audit_events'],
    sourceIds: [
      'aged_care_act_2024_core',
      'aged_care_rules_2025_current',
      'health_new_act_provider_resources',
    ],
    notes:
      'The contractor surface is not yet at the same depth as core provider workflows. Provider oversight, contract terms, and third-party evidence remain manual controls.',
    manualSteps: [
      {
        id: 'associated-provider-oversight-evidence',
        title: 'Record associated-provider oversight',
        description:
          'Confirm the subcontracted service scope, contract obligations, worker screening evidence, incident/complaint referral pathway, and audit evidence before treating the associated-provider arrangement as compliant.',
        ownerRole: 'Operations Director',
        evidenceHint:
          'Keep contract, scope, audit, incident-routing, and worker-clearance evidence.',
        required: true,
      },
    ],
  },
  {
    controlId: 'digital_platform_operator_obligations',
    modulePath: '/settings/system',
    title: 'Aged care digital platform operator obligations',
    obligation:
      'Track whether Statura or a customer-operated marketplace/workforce platform is acting as an aged care digital platform operator and evidence required notices, credential display checks, complaints, incidents, reporting, and recordkeeping.',
    effectiveDateLogic:
      'Digital-platform obligations apply only where the platform is operated in a way that facilitates entities representing they can deliver funded aged care services.',
    programScopes: ['DPO', 'PLATFORM'],
    status: 'manual_input_required',
    dataModels: [
      'tenants',
      'contractors',
      'worker_screenings',
      'complaints',
      'incidents',
      'audit_logs',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'Statura currently treats this as a product/legal boundary requiring tenant-by-tenant assessment. No automatic regulator notification is implemented.',
    manualSteps: [
      {
        id: 'dpo-applicability-assessment',
        title: 'Assess digital-platform-operator applicability',
        description:
          'Confirm whether the deployment facilitates external entities representing they can deliver funded aged care services and, if so, retain operator notice, credential-check, complaint, incident, and reporting evidence.',
        ownerRole: 'Tenant Admin',
        externalSystem: 'ACQSC / provider legal review',
        evidenceHint:
          'Keep the applicability assessment, regulator notice, and operational procedure links.',
        required: true,
      },
    ],
  },
  {
    controlId: 'rac_admission_resident_agreements',
    modulePath: '/residential/admissions',
    title: 'RAC admission, resident agreements, and entry evidence',
    obligation:
      'Complete residential admission, representative authority, care type, resident agreement, accommodation agreement, entry-date cohort, handover, and document-readiness evidence before finalising RAC entry.',
    effectiveDateLogic:
      'Residential entry obligations depend on care type, admission date, respite/permanent status, fee cohort, and agreement transition rules.',
    programScopes: ['RAC'],
    status: 'manual_input_required',
    dataModels: [
      'residential_admission_intakes',
      'residential_admission_intake_items',
      'rac_resident_opening_states',
      'signature_blocks',
      'documents',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'Controlled admission and opening-state gates exist. The remaining manual boundary is verifying source documents and regulator/transition evidence before relying on the intake as final legal evidence.',
    manualSteps: [
      {
        id: 'rac-admission-source-document-review',
        title: 'Verify admission and agreement source records',
        description:
          'Confirm signed agreements, representative authority, entry cohort, and transfer/respite evidence before finalising or certifying the resident entry record.',
        ownerRole: 'Facility Manager',
        evidenceHint: 'Attach signed agreement, authority, admission, and handover documents.',
        required: true,
      },
    ],
  },
  {
    controlId: 'rac_occupancy_transitions_discharge',
    modulePath: '/residential/occupancy',
    title: 'RAC occupancy, transitions, and discharge evidence',
    obligation:
      'Maintain residential occupancy, bed, transfer, leave, respite, discharge, death, and transition evidence with atomic ledger updates and source-document review.',
    effectiveDateLogic:
      'Residential occupancy and transition obligations are event-driven by admission, room/bed movement, leave, respite conversion, discharge, death, and transfer dates.',
    programScopes: ['RAC'],
    status: 'manual_input_required',
    dataModels: [
      'residential_occupancy_events',
      'residential_bed_status_events',
      'client_transitions',
      'resident_absence_events',
      'residential_respite_episodes',
      'documents',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'Controlled occupancy and transition ledgers exist. Providers still need to validate transfer, discharge, death, respite, and leave source evidence before treating the movement record as final legal evidence.',
    manualSteps: [
      {
        id: 'rac-transition-source-review',
        title: 'Verify occupancy and transition source records',
        description:
          'Confirm movement, leave, transfer, discharge, death, or respite evidence before relying on the occupancy ledger for compliance closeout.',
        ownerRole: 'Facility Manager',
        evidenceHint:
          'Attach transfer forms, leave records, discharge notices, death notices, or respite evidence.',
        required: true,
      },
    ],
  },
  {
    controlId: 'rac_care_minutes_rn_coverage_subsidy',
    modulePath: '/compliance/rn-coverage',
    title: 'RAC care minutes, RN coverage, AN-ACC, and subsidy evidence',
    obligation:
      'Maintain AN-ACC classification, care-minute targets, delivered care minutes, RN coverage, worker capability, roster evidence, and subsidy/reporting readiness.',
    effectiveDateLogic:
      'Targets and subsidy evidence are classification, facility, staff capability, roster period, and reporting-period specific.',
    programScopes: ['RAC'],
    status: 'manual_input_required',
    dataModels: [
      'res_anacc_classifications',
      'care_minute_targets',
      'daily_care_minute_records',
      'direct_care_attributions',
      'roster_shift_assignments',
      'worker_screenings',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'RAC care-minute and RN evidence is in-platform, but final regulator/subsidy reporting and external staff registration verification remain manual controls.',
    manualSteps: [
      {
        id: 'rac-care-minute-submission-review',
        title: 'Review care-minute and RN evidence before submission',
        description:
          'Confirm roster, direct-care attribution, AHPRA/manual RN status, AN-ACC classification, and reporting-period totals before external submission or board sign-off.',
        ownerRole: 'Clinical Lead',
        externalSystem: 'GPMS / provider reporting channel',
        evidenceHint:
          'Keep the submission pack, roster exception list, and acknowledgement reference.',
        required: true,
      },
    ],
  },
  {
    controlId: 'clinical_medication_wound_governance',
    modulePath: '/compliance/clinical-governance',
    title: 'Clinical, medication, wound, and deterioration governance',
    obligation:
      'Maintain clinical governance evidence for medication administration, S8 controls, allergies, wounds, observations, deterioration, infection prevention, clinical review, and care-plan follow-up.',
    effectiveDateLogic:
      'Clinical obligations are person, episode, medication, wound, observation, incident, review, and care-plan-state specific.',
    programScopes: ['RAC', 'SAH'],
    status: 'manual_input_required',
    dataModels: [
      'medications',
      'medication_administrations',
      'controlled_drug_register',
      'wounds',
      'wound_assessments',
      'observations',
      'care_plans',
      'clinical_alerts',
      'ipc_infections',
    ],
    sourceIds: [
      'aged_care_act_2024_core',
      'aged_care_rules_2025_current',
      'acqsc_strengthened_provider_guidance',
    ],
    notes:
      'Clinical workflow evidence exists across medication, S8, wound, observation, IPC, and care-plan modules. Provider clinical judgement, review sign-off, and any external clinical records remain manual controls.',
    manualSteps: [
      {
        id: 'clinical-governance-signoff',
        title: 'Review clinical source evidence and follow-through',
        description:
          'Confirm medication, wound, deterioration, IPC, and care-plan follow-through evidence before treating the clinical record set as audit-ready.',
        ownerRole: 'Clinical Lead',
        evidenceHint:
          'Keep clinical review notes, medication/S8 checks, wound photos, observations, and care-plan links.',
        required: true,
      },
    ],
  },
  {
    controlId: 'rac_quality_indicators_and_gpms',
    modulePath: '/compliance/quality',
    title: 'RAC quality indicators and GPMS preparation',
    obligation:
      'Prepare residential quality indicator records, quarter submissions, survey/instrument evidence, and GPMS-ready packs without presenting in-platform values as official GPMS submission or star-rating results.',
    effectiveDateLogic:
      'QI obligations are quarter, facility, resident, indicator, instrument, and submission-window specific.',
    programScopes: ['RAC'],
    status: 'external_submission_required',
    governmentIntegrationExcluded: true,
    dataModels: ['quality_indicator_submissions', 'quality_indicator_records', 'incidents'],
    sourceIds: [
      'aged_care_act_2024_core',
      'aged_care_rules_2025_current',
      'acqsc_strengthened_quality_standards',
      'department_qi_program',
    ],
    notes:
      'Quarter records and source-backed indicators are prepared in-platform. Final GPMS lodgement remains external under the government-integration exclusion.',
    manualSteps: [
      {
        id: 'rac-gpms-external-submit',
        title: 'Submit QI pack externally and capture acknowledgement',
        description:
          'Review the quarter pack, submit through the required government channel, and record receipt, rejection, or follow-up evidence.',
        ownerRole: 'Quality Officer',
        externalSystem: 'GPMS',
        evidenceHint: 'Retain GPMS acknowledgement, validation report, or rejection response.',
        required: true,
      },
    ],
  },
  {
    controlId: 'dex_reporting_external_submission',
    modulePath: '/compliance/reporting/dex',
    title: 'DEX reporting preparation and external submission',
    obligation:
      'Prepare DEX case, session, outlet, outcome, assessment, and bulk-upload payload evidence while keeping final DSS Data Exchange submission and acceptance external.',
    effectiveDateLogic:
      'DEX obligations are program, funding-agreement, outlet, client, session, assessment, and reporting-period specific.',
    programScopes: ['PLATFORM'],
    status: 'external_submission_required',
    governmentIntegrationExcluded: true,
    dataModels: [
      'clients',
      'facilities',
      'services_delivered',
      'dex_outlet_configs',
      'dex_client_profiles',
      'dex_case_profiles',
      'dex_service_mappings',
      'dex_session_overrides',
      'dex_assessment_records',
    ],
    sourceIds: ['dss_dex_bulk_upload'],
    notes:
      'The platform prepares DEX bulk-upload evidence. Final upload, acceptance, and rejection handling remain external until a government integration is approved.',
    manualSteps: [
      {
        id: 'dex-external-submit',
        title: 'Upload DEX payload externally and capture acceptance',
        description:
          'Generate the DEX payload, submit it through the DSS Data Exchange channel, and record confirmation, rejection, or validation evidence.',
        ownerRole: 'Quality Officer',
        externalSystem: 'DSS Data Exchange',
        evidenceHint: 'Retain the DSS submission confirmation and any returned validation report.',
        required: true,
      },
    ],
  },
  {
    controlId: 'rac_prudential_accommodation_financial',
    modulePath: '/compliance/prudential',
    title: 'RAC prudential, accommodation, and financial evidence',
    obligation:
      'Maintain RAD/DAP, accommodation payment, refund, room-price, prudential statement, liquidity, resident disclosure, and external filing evidence.',
    effectiveDateLogic:
      'RAC prudential obligations are resident, facility, payment-arrangement, refund-event, and reporting-period specific.',
    programScopes: ['RAC'],
    status: 'manual_input_required',
    dataModels: [
      'accommodation_deposits',
      'prudential_refund_events',
      'prudential_refund_deductions',
      'prudential_compliance_statements',
      'prudential_external_submissions',
      'residential_room_prices',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'Internal prudential lifecycle, statements, resident disclosures, and refund evidence exist. Final external filings, board records, and regulator acknowledgements remain manual evidence under the government-integration exclusion.',
    manualSteps: [
      {
        id: 'rac-prudential-external-filing',
        title: 'Record prudential filing or board evidence',
        description:
          'Review the prudential pack, complete any required external filing or board sign-off outside Statura, and capture the reference or supporting evidence.',
        ownerRole: 'Finance Officer',
        externalSystem: 'ACFR / provider portal / board records',
        evidenceHint: 'Retain the submission receipt, board minute, or regulator filing reference.',
        required: true,
      },
    ],
  },
  {
    controlId: 'sah_support_plan_classification_budget',
    modulePath: '/home-care',
    title: 'SAH support plan, classification, and budget controls',
    obligation:
      'Maintain SAH support-plan acceptance, classifications, priority-category source evidence including the 2026 MND priority amendment, quarterly budgets, care-management pooling, support-plan review requests, budget-review requests, and pathway evidence.',
    effectiveDateLogic:
      'Support at Home support-plan, classification, priority-category, and budget obligations apply from 1 November 2025. The 2026 Motor Neurone Disease priority amendment commenced on 6 June 2026 and is represented as source evidence for priority-category review.',
    status: 'manual_input_required',
    programScopes: ['SAH'],
    implementationPhase: 'SAH_RELEASE_CANDIDATE',
    supportAtHomeDeferred: false,
    governmentIntegrationExcluded: true,
    dataModels: [
      'care_plans',
      'sah_classifications',
      'sah_quarter_budgets',
      'sah_client_budgets',
      'sah_review_requests',
    ],
    sourceIds: [
      'aged_care_act_2024_core',
      'aged_care_rules_2025_current',
      'aged_care_rules_2026_mnd_priority',
    ],
    notes:
      'The platform exposes support-plan, classification, budget, review-request, and readiness evidence. External assessor outcomes, priority-category source evidence, and provider support-plan judgement remain manual evidence.',
    manualSteps: [
      {
        id: 'sah-support-plan-source-review',
        title: 'Review SAH support-plan source evidence',
        description:
          'Confirm support-plan acceptance, classification, MND priority-category handling where applicable, budget review, and pathway evidence before treating the SAH setup as compliance-ready.',
        ownerRole: 'Coordinator',
        evidenceHint:
          'Keep support-plan acceptance, classification, priority-category, budget review, and pathway evidence.',
        required: true,
      },
    ],
  },
  {
    controlId: 'sah_claims_statements_contributions',
    modulePath: '/home-care/claims',
    title: 'SAH claims, statements, and contributions',
    obligation:
      'Maintain SAH service-ledger traceability, participant contribution evidence, statements, claim batches, external provider-portal attestation, acknowledgement, rejection, payment, and reconciliation evidence.',
    effectiveDateLogic:
      'Support at Home claims, statements, and contribution controls apply from 1 November 2025 and remain subject to manual provider-portal submission while government integrations are excluded.',
    status: 'external_submission_required',
    programScopes: ['SAH'],
    implementationPhase: 'SAH_RELEASE_CANDIDATE',
    supportAtHomeDeferred: false,
    governmentIntegrationExcluded: true,
    dataModels: [
      'services_delivered',
      'sah_service_details',
      'sah_contribution_events',
      'sah_monthly_statements',
      'sah_claim_batches',
      'sah_claim_batch_submissions',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'Claim and statement preparation are in-platform. Final Services Australia provider-portal lodgement, acknowledgement, rejection, payment, and reconciliation references remain manual/external under the government-integration exclusion.',
    manualSteps: [
      {
        id: 'sah-provider-portal-attestation',
        title: 'Capture provider-portal attestation evidence',
        description:
          'Submit claims outside Statura while government integrations remain excluded, then capture acknowledgement, rejection, payment, or reconciliation evidence.',
        ownerRole: 'Finance Officer',
        externalSystem: 'Services Australia provider portal',
        evidenceHint:
          'Keep the manual external attestation, acknowledgement, rejection, or payment reference.',
        required: true,
      },
    ],
  },
  {
    controlId: 'sah_at_hm_restorative_eol_pathways',
    modulePath: '/home-care/at-hm',
    title: 'SAH AT-HM, restorative care, and end-of-life pathways',
    obligation:
      'Maintain AT-HM approval, procurement, installation, claim effect, restorative-care, and end-of-life pathway evidence with provider review and external approval boundaries.',
    effectiveDateLogic:
      'Support at Home AT-HM, restorative-care, and end-of-life pathway obligations apply from 1 November 2025 and require retained approval, procurement, clinical-pathway, and provider-review evidence.',
    status: 'manual_input_required',
    programScopes: ['SAH'],
    implementationPhase: 'SAH_RELEASE_CANDIDATE',
    supportAtHomeDeferred: false,
    governmentIntegrationExcluded: true,
    dataModels: [
      'sah_at_hm_requests',
      'restorative_care_episodes',
      'care_plan_service_inclusions',
      'clinical_pathway_enrolments',
    ],
    sourceIds: ['aged_care_act_2024_core', 'aged_care_rules_2025_current'],
    notes:
      'AT-HM and restorative-care evidence is represented in-platform. External approval, supplier, assessor, and clinical-source records remain manual evidence where they are not generated inside Statura.',
    manualSteps: [
      {
        id: 'sah-pathway-approval-evidence',
        title: 'Review approval and pathway evidence',
        description:
          'Confirm approval, prescribed-need, supplier, installation, claim, restorative-care, and end-of-life pathway evidence before treating the pathway as compliance-ready.',
        ownerRole: 'Case Manager',
        evidenceHint:
          'Keep approval, prescribed-need, supplier, installation, claim, or clinical pathway evidence.',
        required: true,
      },
    ],
  },
  {
    controlId: 'sah_claims',
    modulePath: '/home-care/claims',
    title: 'Support at Home claims',
    obligation: 'Prepare claim-ready Support at Home service rows and provider claim batches.',
    effectiveDateLogic: 'Aged Care Rules 2025 Chapter 4 from 1 November 2025 onward.',
    status: 'manual_input_required',
    dataModels: ['services_delivered', 'sah_service_details', 'sah_claim_batches', 'clients'],
    sourceIds: ['sah_rules_2025_ch4', 'sah_pooled_care_management_2025_09'],
    supersededByControlId: 'sah_claims_statements_contributions',
    notes:
      'Claim reference data is enforced in-app and pooled care-management rows are excluded from ACPP invoice batches, but provider-side validation remains required before submission.',
    manualSteps: [
      {
        id: 'sah-claims-validate-reference-data',
        title: 'Validate claim reference data',
        description:
          'Confirm the service identifier, funding source code, and participant identifiers match the provider claim pack before submission.',
        ownerRole: 'Finance Officer',
        externalSystem: 'Services Australia / PRODA',
        evidenceHint:
          'Retain the accepted batch reference or rejection message against the batch record.',
        required: true,
      },
    ],
  },
  {
    controlId: 'sah_monthly_statements',
    modulePath: '/home-care/statements',
    title: 'Support at Home monthly statements',
    obligation:
      'Issue participant statements meeting Aged Care Rules 2025 s 155-40 disclosure requirements.',
    effectiveDateLogic: 'Monthly statements for Support at Home from 1 November 2025 onward.',
    status: 'automated',
    dataModels: [
      'sah_monthly_statements',
      'sah_client_budgets',
      'sah_contribution_events',
      'services_delivered',
    ],
    sourceIds: [
      'sah_rules_2025_ch4',
      'sah_statement_guide_2025_10',
      'sah_pooled_care_management_2025_09',
    ],
    supersededByControlId: 'sah_claims_statements_contributions',
    notes:
      'Monthly statements now disclose pooled care management by service-delivery branch and flag deemed-delivered late cancellations or no-shows directly on the statement payload.',
  },
  {
    controlId: 'sah_at_hm',
    modulePath: '/home-care/at-hm',
    title: 'Assistive Technology and Home Modifications',
    obligation:
      'Track requests, commitments, participant contributions, delivery, and claim state for AT-HM support.',
    effectiveDateLogic: 'Support at Home AT-HM rules from 1 November 2025 onward.',
    status: 'manual_input_required',
    dataModels: ['sah_at_hm_requests', 'sah_service_details', 'sah_claim_batches'],
    sourceIds: ['sah_rules_2025_ch4', 'sah_costs_contributions_2025_11'],
    supersededByControlId: 'sah_at_hm_restorative_eol_pathways',
    notes:
      'Committed spend is now truthful, but sourcing, installation, higher-cost approvals, and provider claim reconciliation still need manual provider controls.',
    manualSteps: [
      {
        id: 'athm-retain-approval-evidence',
        title: 'Retain approval and prescribed-need evidence',
        description:
          'Confirm the AT or home modification tier, any prescribed-need justification above the nominal cap, and the approval evidence before procurement or claim submission.',
        ownerRole: 'Case Manager',
        externalSystem: 'Services Australia / provider assessment records',
        evidenceHint:
          'Store the approval note, prescription, or complex-modification evidence with the request.',
        required: true,
      },
    ],
  },
  {
    controlId: 'client_contributions_register',
    modulePath: '/finance/contributions',
    title: 'Client contribution register and overrides',
    obligation:
      'Record assessed residential and Support at Home contribution amounts, hardship reductions, and cap tracking without replacing official assessment notices.',
    effectiveDateLogic:
      'Residential fee advice and Support at Home contribution settings are date-effective and cohort-specific.',
    status: 'manual_input_required',
    dataModels: ['client_contributions', 'clients'],
    sourceIds: [
      'sah_costs_contributions_2025_11',
      'sah_rules_2025_ch4',
      'residential_schedule_2026_03_20',
    ],
    notes:
      'This screen is a contribution ledger and override surface. Official residential fee advice and Support at Home participant contribution assessments still need provider review before entry.',
    manualSteps: [
      {
        id: 'client-contributions-verify-notice',
        title: 'Verify against the current assessment notice',
        description:
          'Check Services Australia, DVA, or provider fee advice before recording or changing a contribution entry.',
        ownerRole: 'Finance Officer',
        externalSystem: 'Services Australia / DVA / provider fee advice',
        evidenceHint:
          'Retain the notice date or supporting fee advice against the contribution change.',
        required: true,
      },
    ],
  },
  {
    controlId: 'residential_fee_billing',
    modulePath: '/finance/contributions/billing-engine',
    title: 'Residential fee billing',
    obligation:
      'Bill residential basic daily fee, hotelling contribution, MTCF, and NCCC correctly by resident regime and cap state.',
    effectiveDateLogic:
      'Resident regime depends on admission/protected status and quarterly indexed fee schedules.',
    status: 'automated',
    dataModels: ['client_contributions', 'clients'],
    sourceIds: ['residential_schedule_2026_03_20'],
    supersededByControlId: 'rac_prudential_accommodation_financial',
  },
  {
    controlId: 'prudential_deposits',
    modulePath: '/compliance/prudential/deposits',
    title: 'Accommodation deposit register and refunds',
    obligation:
      'Track RAD/DAP deposits, refund deadlines, and refund interest on overdue balances.',
    effectiveDateLogic: 'Refund deadlines are resident-event driven; MPIR changes quarterly.',
    status: 'automated',
    dataModels: [
      'accommodation_deposits',
      'prudential_refund_deductions',
      'prudential_refund_events',
    ],
    sourceIds: ['residential_schedule_2026_03_20', 'residential_mpir_2026_q2'],
    supersededByControlId: 'rac_prudential_accommodation_financial',
    notes:
      'Deposit refunds now keep a first-class deduction ledger and immutable refund event snapshot rather than relying on free-text notes.',
  },
  {
    controlId: 'prudential_annual_statements',
    modulePath: '/compliance/prudential/reports',
    title: 'Prudential annual statements',
    obligation:
      'Prepare annual RAD/DAP statement summaries for provider reconciliation and resident reporting.',
    effectiveDateLogic: 'Annual reporting by facility and financial year.',
    status: 'manual_input_required',
    dataModels: [
      'accommodation_deposits',
      'residential_accommodation_agreements',
      'prudential_refund_deductions',
      'prudential_refund_events',
      'prudential_resident_disclosures',
      'prudential_compliance_statements',
    ],
    sourceIds: ['residential_schedule_2026_03_20'],
    supersededByControlId: 'rac_prudential_accommodation_financial',
    notes:
      'The system now generates resident disclosure snapshots from the linked accommodation agreements, refund deduction ledger, and refund event history, and it records issue and acknowledgement metadata in-platform. Provider reconciliation against the signed agreement and any external permitted-use adjustments still remains an explicit review step before final issue.',
    manualSteps: [
      {
        id: 'prudential-statement-reconcile',
        title: 'Reconcile against signed agreements and refunds',
        description:
          'Confirm each resident statement against the linked signed accommodation agreement, refund records, and any facility-level permitted-use adjustments before issuing it to the resident.',
        ownerRole: 'Finance Officer',
        evidenceHint: 'Retain the reconciliation worksheet or approver sign-off.',
        required: true,
      },
    ],
  },
  {
    controlId: 'prudential_accommodation_pricing',
    modulePath: '/compliance/prudential/pricing',
    title: 'Residential accommodation pricing and agreements',
    obligation:
      'Maintain approved room pricing, RAD/DAP calculations, and resident accommodation agreement terms.',
    effectiveDateLogic:
      'Room price approvals and MPIR-based DAPs are date-effective and resident-specific.',
    status: 'manual_input_required',
    dataModels: [
      'residential_room_prices',
      'residential_accommodation_agreements',
      'accommodation_deposits',
    ],
    sourceIds: ['residential_schedule_2026_03_20', 'residential_mpir_2026_q2'],
    supersededByControlId: 'rac_prudential_accommodation_financial',
    notes:
      'Room-specific pricing, resident accommodation terms, and signed agreement evidence can now be stored against the linked deposit/agreement record. The remaining manual step is validating that any external room-price approval reference and uploaded agreement evidence are the correct legal record.',
    manualSteps: [
      {
        id: 'prudential-pricing-external-record',
        title: 'Validate uploaded agreement evidence',
        description:
          'Validate the uploaded signed accommodation agreement and any external room-price approval reference against the in-app agreement record before relying on it as the final legal evidence set.',
        ownerRole: 'Facility Manager',
        evidenceHint:
          'Keep the signed agreement document and room-price approval reference linked to the resident accommodation arrangement.',
        required: true,
      },
    ],
  },
  {
    controlId: 'prudential_governance',
    modulePath: '/compliance/prudential/governance',
    title: 'Prudential governance artefacts and attestations',
    obligation:
      'Maintain prudential policies, attestation records, and annual prudential compliance statements with clear evidence and approval state.',
    effectiveDateLogic:
      'Prudential governance documents and annual statements are reporting-period and review-date driven.',
    status: 'manual_input_required',
    dataModels: [
      'policy_register',
      'governance_attestations',
      'prudential_compliance_statements',
      'liquidity_records',
    ],
    sourceIds: ['residential_schedule_2026_03_20', 'residential_mpir_2026_q2'],
    supersededByControlId: 'rac_prudential_accommodation_financial',
    notes:
      'The platform now stores prudential governance artefacts, prudential attestations, and compliance statements in managed records, including managed document linkage and explicit external submission state on prudential statements. Providers still need to validate the linked evidence and complete any external filing obligations that remain outside the system.',
    manualSteps: [
      {
        id: 'prudential-governance-policy-review',
        title: 'Validate linked prudential artefacts',
        description:
          'Confirm that the linked managed document or external source for each prudential artefact is the current approved source record before relying on it for prudential review.',
        ownerRole: 'Governance Lead',
        evidenceHint:
          'Keep the current approved managed document or external repository reference on the prudential artefact record.',
        required: true,
      },
      {
        id: 'prudential-governance-external-filing',
        title: 'Complete any external prudential filing',
        description:
          'After preparing and approving the in-platform prudential statement, record any required external submission or board sign-off reference and complete the filing outside the system where the regulator or board workflow is still external.',
        ownerRole: 'Finance Officer',
        evidenceHint: 'Retain the submission receipt, board minute, or regulator filing reference.',
        required: true,
      },
    ],
  },
  {
    controlId: 'workforce_payroll',
    modulePath: '/roster/timesheets',
    title: 'Award and EA payroll completeness',
    obligation:
      'Apply date-effective award or enterprise-agreement classifications, ordinary hours, overtime, and payroll-run allowance handling correctly.',
    effectiveDateLogic:
      'Award rates are date-effective; daily/weekly logic depends on employment type and agreement state.',
    status: 'manual_input_required',
    dataModels: ['timesheets', 'staff', 'roster_shifts'],
    sourceIds: ['fair_work_pay_guides_current', 'fair_work_schads_2025_10'],
    notes:
      'Per-shift compliance, dispute visibility, full-time ordinary-hours arrangements, EA component evidence, and payroll-ready export gating are now in-platform, but payroll-run allowance reconciliation and final external payroll mapping still remain explicit provider review steps.',
    manualSteps: [
      {
        id: 'workforce-payroll-final-review',
        title: 'Review payroll-run edge cases',
        description:
          'Check full-time ordinary-hours agreements, travel and broken-shift allowances, and any external payroll mappings before final payroll import.',
        ownerRole: 'Payroll Officer',
        evidenceHint: 'Retain the payroll review sign-off or exception note for the pay run.',
        required: true,
      },
    ],
  },
  {
    controlId: 'quality_indicators',
    modulePath: '/compliance/quality',
    title: 'Mandatory quality indicators',
    obligation:
      'Prepare quarter-accurate quality indicator data without synthesising unsupported metrics.',
    effectiveDateLogic:
      'Quarter-bounded indicator collection with formal survey/instrument rules for selected domains.',
    status: 'automated',
    dataModels: [
      'quality_indicator_records',
      'quality_indicator_submissions',
      'observations',
      'incidents',
      'restrictive_practices',
    ],
    sourceIds: ['aihw_qi_program'],
    supersededByControlId: 'rac_quality_indicators_and_gpms',
    notes:
      'Source-backed indicators are derived from live records, and survey/instrument-backed indicators can now be stored and governed as quarter records in-platform rather than being synthesised or left outside the submission model.',
  },
  {
    controlId: 'gpms_preparation',
    modulePath: '/compliance/quality',
    title: 'GPMS preparation',
    obligation:
      'Prepare facility submissions from stored quarter records rather than speculative dashboard values.',
    effectiveDateLogic: 'Quarter-specific submission records and indicator domains.',
    status: 'external_submission_required',
    dataModels: ['quality_indicator_submissions', 'quality_indicator_records'],
    sourceIds: ['aihw_qi_program'],
    supersededByControlId: 'rac_quality_indicators_and_gpms',
    notes:
      'The platform now prepares quarter-bounded records, but the final submission occurs through external government channels.',
  },
  {
    controlId: 'dex_reporting',
    modulePath: '/compliance/reporting/dex',
    title: 'DSS DEX reporting',
    obligation: 'Produce a compliant DSS DEX submission covering case/session/outcome payloads.',
    effectiveDateLogic:
      'Reporting periods are quarterly; payload requires more than SLK-ready demographics.',
    status: 'external_submission_required',
    dataModels: [
      'clients',
      'facilities',
      'services_delivered',
      'dex_outlet_configs',
      'dex_client_profiles',
      'dex_case_profiles',
      'dex_service_mappings',
      'dex_session_overrides',
      'dex_assessment_records',
    ],
    sourceIds: ['dss_dex_bulk_upload'],
    supersededByControlId: 'dex_reporting_external_submission',
    notes:
      'The platform now prepares a real DSS DEX bulk-upload payload with outlet, client, case, session, and assessment nodes. Final submission and acceptance still occur in the DSS Data Exchange channel.',
    manualSteps: [
      {
        id: 'dex-external-submit',
        title: 'Upload the prepared DEX XML externally',
        description:
          'Generate the in-platform DEX XML, then complete the actual upload and acceptance workflow in the DSS Data Exchange environment.',
        ownerRole: 'Quality Officer',
        externalSystem: 'DSS Data Exchange',
        evidenceHint: 'Retain the DSS submission confirmation and any returned validation report.',
        required: true,
      },
    ],
  },
  {
    controlId: 'restrictive_practices',
    modulePath: '/compliance/restrictive-practices',
    title: 'Restrictive practice governance',
    obligation:
      'Capture authorisation, review cadence, and governance evidence for restrictive practices.',
    effectiveDateLogic: 'Authorisation and review cadence are resident- and practice-specific.',
    status: 'automated',
    dataModels: ['restrictive_practices'],
    sourceIds: ['aihw_qi_program'],
    supersededByControlId: 'restrictive_practices_behaviour_support',
  },
  {
    controlId: 'sirs_incidents',
    modulePath: '/compliance/incidents',
    title: 'SIRS incident reporting',
    obligation: 'Determine reportability, priority, and response timing for SIRS incidents.',
    effectiveDateLogic:
      'Priority and deadline logic follows the incident type and harm characteristics in force from 1 November 2025.',
    status: 'manual_input_required',
    dataModels: ['incidents'],
    sourceIds: ['aged_care_act_2024_sirs', 'acqsc_sirs_guidance'],
    supersededByControlId: 'incident_management_sirs_open_disclosure',
    notes:
      'The platform derives SIRS reportability and deadlines from the 8 lawful categories, but ambiguous operational incidents still need provider review to ensure they are mapped into the correct category before any Commission submission.',
    manualSteps: [
      {
        id: 'sirs-remap-ambiguous-incident',
        title: 'Map ambiguous incidents into a lawful SIRS category before submission',
        description:
          'If the presenting event is recorded as a fall, medication error, infection, pressure injury, behavioural event, or other operational incident, review the facts and remap it into one of the 8 lawful SIRS categories before treating it as Commission-reportable.',
        ownerRole: 'Compliance Officer',
        externalSystem: 'ACQSC SIRS portal',
        evidenceHint:
          'Retain the reasoning for the final SIRS category selection and any supporting notes used to justify submission.',
        required: true,
      },
    ],
  },
];

export function getLegislativeSource(sourceId: string): SourceVerificationMeta | null {
  return LEGISLATIVE_SOURCES[sourceId] ?? null;
}

export function getCanonicalLegislativeControlId(controlId: string): string {
  return (
    LEGACY_LEGISLATIVE_CONTROL_ALIASES[controlId] ??
    LEGACY_LEGISLATIVE_MODULE_PATH_ALIASES[controlId] ??
    controlId
  );
}

function withNewActWorkflowEvidence(control: LegislativeControlRecord): LegislativeControlRecord {
  const isNewActCanonicalControl = NEW_ACT_CANONICAL_CONTROL_ID_SET.has(control.controlId);
  return {
    ...control,
    governmentIntegrationExcluded: isNewActCanonicalControl
      ? true
      : control.governmentIntegrationExcluded,
    workflowEvidenceRoutes:
      NEW_ACT_WORKFLOW_EVIDENCE_ROUTES[control.controlId as NewActCanonicalControlId] ??
      control.workflowEvidenceRoutes ??
      [],
  };
}

export function getNewActCanonicalControlMatrix(): LegislativeControlRecord[] {
  const controls = NEW_ACT_CANONICAL_CONTROL_IDS.map((controlId) =>
    LEGISLATIVE_CONTROL_MATRIX.find((control) => control.controlId === controlId),
  )
    .filter((control): control is LegislativeControlRecord => Boolean(control))
    .map(withNewActWorkflowEvidence);
  if (controls.length !== NEW_ACT_CANONICAL_CONTROL_IDS.length) {
    const found = new Set(controls.map((control) => control.controlId));
    const missing = NEW_ACT_CANONICAL_CONTROL_IDS.filter((controlId) => !found.has(controlId));
    throw new Error(`New Act canonical control matrix is missing: ${missing.join(', ')}`);
  }
  return controls;
}

export function getLegislativeControl(controlIdOrPath: string): LegislativeControlRecord | null {
  const canonicalControlId = getCanonicalLegislativeControlId(controlIdOrPath);
  const canonicalControl = LEGISLATIVE_CONTROL_MATRIX.find(
    (control) => control.controlId === canonicalControlId,
  );
  if (canonicalControl) return withNewActWorkflowEvidence(canonicalControl);

  const control =
    LEGISLATIVE_CONTROL_MATRIX.find(
      (item) => item.controlId === controlIdOrPath || item.modulePath === controlIdOrPath,
    ) ?? null;
  return control ? withNewActWorkflowEvidence(control) : null;
}

export function getLegislativeStatusSummary(status: LegislativeControlStatus) {
  return LEGISLATIVE_STATUS_SUMMARIES[status];
}

function isSourceVerificationMeta(
  source: SourceVerificationMeta | undefined,
): source is SourceVerificationMeta {
  return Boolean(source);
}

export function expandLegislativeControl(controlIdOrPath: string) {
  const control = getLegislativeControl(controlIdOrPath);
  if (!control) return null;
  return {
    ...control,
    sources: control.sourceIds
      .map((sourceId: string) => LEGISLATIVE_SOURCES[sourceId])
      .filter(isSourceVerificationMeta),
  };
}

export function summariseLegislativeCompleteness(
  controls: LegislativeControlRecord[] = getNewActCanonicalControlMatrix(),
): LegislativeCompletenessSummary {
  const automatedCount = controls.filter((control) => control.status === 'automated').length;
  const manualInputRequiredCount = controls.filter(
    (control) => control.status === 'manual_input_required',
  ).length;
  const externalSubmissionRequiredCount = controls.filter(
    (control) => control.status === 'external_submission_required',
  ).length;
  const unsupportedCount = controls.filter((control) => control.status === 'not_supported').length;

  const residualControls = controls
    .filter(
      (control) =>
        control.status === 'manual_input_required' ||
        control.status === 'external_submission_required' ||
        control.status === 'not_supported',
    )
    .map(
      (control) =>
        ({
          ...control,
          sources: control.sourceIds
            .map((sourceId: string) => LEGISLATIVE_SOURCES[sourceId])
            .filter(isSourceVerificationMeta),
        }) satisfies ExpandedLegislativeControl,
    )
    .sort((a, b) => {
      const rank = (status: LegislativeControlStatus) =>
        status === 'not_supported' ? 0 : status === 'manual_input_required' ? 1 : 2;
      return rank(a.status) - rank(b.status) || a.title.localeCompare(b.title);
    });

  return {
    totalControls: controls.length,
    automatedCount,
    manualInputRequiredCount,
    externalSubmissionRequiredCount,
    unsupportedCount,
    residualBoundaryCount:
      manualInputRequiredCount + externalSubmissionRequiredCount + unsupportedCount,
    sourceCount: Object.keys(LEGISLATIVE_SOURCES).length,
    manualStepCount: controls.reduce((sum, control) => sum + (control.manualSteps?.length ?? 0), 0),
    residualControls,
  };
}

// Department of Health, Disability and Ageing, "BIR and MPIR table of current and
// previous rates" (doh-bir-mpir-2026-09.pdf, page 2), transcribed in full on
// 2026-09-30. SQL mirror: bir_rate_history / mpir_rate_history. A new row is
// published each quarter; a date past the last row throws rather than guess.
const RESIDENTIAL_INTEREST_RATE_HISTORY = [
  { effectiveFrom: '2019-07-01', effectiveTo: '2019-07-31', birPct: 3.75, mpirPct: 5.54 },
  { effectiveFrom: '2019-08-01', effectiveTo: '2019-09-30', birPct: 3.0, mpirPct: 5.54 },
  { effectiveFrom: '2019-10-01', effectiveTo: '2019-12-31', birPct: 3.0, mpirPct: 4.98 },
  { effectiveFrom: '2020-01-01', effectiveTo: '2020-03-31', birPct: 3.0, mpirPct: 4.91 },
  { effectiveFrom: '2020-04-01', effectiveTo: '2020-05-31', birPct: 3.0, mpirPct: 4.89 },
  { effectiveFrom: '2020-06-01', effectiveTo: '2020-06-30', birPct: 2.25, mpirPct: 4.89 },
  { effectiveFrom: '2020-07-01', effectiveTo: '2020-09-30', birPct: 2.25, mpirPct: 4.1 },
  { effectiveFrom: '2020-10-01', effectiveTo: '2020-12-31', birPct: 2.25, mpirPct: 4.1 },
  { effectiveFrom: '2021-01-01', effectiveTo: '2021-03-31', birPct: 2.25, mpirPct: 4.02 },
  { effectiveFrom: '2021-04-01', effectiveTo: '2021-06-30', birPct: 2.25, mpirPct: 4.01 },
  { effectiveFrom: '2021-07-01', effectiveTo: '2021-09-30', birPct: 2.25, mpirPct: 4.04 },
  { effectiveFrom: '2021-10-01', effectiveTo: '2021-12-31', birPct: 2.25, mpirPct: 4.01 },
  { effectiveFrom: '2022-01-01', effectiveTo: '2022-03-31', birPct: 2.25, mpirPct: 4.04 },
  { effectiveFrom: '2022-04-01', effectiveTo: '2022-06-30', birPct: 2.25, mpirPct: 4.07 },
  { effectiveFrom: '2022-07-01', effectiveTo: '2022-09-30', birPct: 2.25, mpirPct: 5.0 },
  { effectiveFrom: '2022-10-01', effectiveTo: '2022-12-31', birPct: 2.25, mpirPct: 6.31 },
  { effectiveFrom: '2023-01-01', effectiveTo: '2023-03-31', birPct: 2.25, mpirPct: 7.06 },
  { effectiveFrom: '2023-04-01', effectiveTo: '2023-06-30', birPct: 2.25, mpirPct: 7.46 },
  { effectiveFrom: '2023-07-01', effectiveTo: '2023-09-30', birPct: 2.25, mpirPct: 7.9 },
  { effectiveFrom: '2023-10-01', effectiveTo: '2023-12-31', birPct: 2.25, mpirPct: 8.15 },
  { effectiveFrom: '2024-01-01', effectiveTo: '2024-03-31', birPct: 2.25, mpirPct: 8.38 },
  { effectiveFrom: '2024-04-01', effectiveTo: '2024-06-30', birPct: 2.25, mpirPct: 8.34 },
  { effectiveFrom: '2024-07-01', effectiveTo: '2024-09-30', birPct: 2.25, mpirPct: 8.36 },
  { effectiveFrom: '2024-10-01', effectiveTo: '2024-12-31', birPct: 2.25, mpirPct: 8.38 },
  { effectiveFrom: '2025-01-01', effectiveTo: '2025-03-31', birPct: 2.25, mpirPct: 8.42 },
  { effectiveFrom: '2025-04-01', effectiveTo: '2025-06-30', birPct: 2.25, mpirPct: 8.17 },
  { effectiveFrom: '2025-07-01', effectiveTo: '2025-09-30', birPct: 2.25, mpirPct: 7.78 },
  { effectiveFrom: '2025-10-01', effectiveTo: '2025-12-31', birPct: 2.75, mpirPct: 7.61 },
  { effectiveFrom: '2026-01-01', effectiveTo: '2026-03-31', birPct: 2.75, mpirPct: 7.65 },
  { effectiveFrom: '2026-04-01', effectiveTo: '2026-06-30', birPct: 3.25, mpirPct: 7.96 },
  { effectiveFrom: '2026-07-01', effectiveTo: '2026-09-30', birPct: 3.25, mpirPct: 8.43 },
  { effectiveFrom: '2026-10-01', effectiveTo: '2026-12-31', birPct: 3.75, mpirPct: 8.51 },
] as const;

function residentialInterestRateFor(kind: 'BIR' | 'MPIR', date: string) {
  const row = RESIDENTIAL_INTEREST_RATE_HISTORY.find(
    (rate) => rate.effectiveFrom <= date && rate.effectiveTo >= date,
  );
  if (!row) throw new Error(`Residential ${kind} rate missing for ${date}`);
  return kind === 'BIR' ? row.birPct : row.mpirPct;
}

function addDaysIso(date: string, days: number) {
  const parsed = new Date(`${date}T00:00:00Z`);

  parsed.setUTCDate(parsed.getUTCDate() + days);
  // eslint-disable-next-line statura/no-utc-date-string -- input was UTC midnight from synthetic `${date}T00:00:00Z`; output remains UTC YYYY-MM-DD by design (regulatory date arithmetic, not "today" derivation)
  return parsed.toISOString().slice(0, 10);
}

function diffIsoDays(fromDate: string, toDate: string) {
  const from = Date.parse(`${fromDate}T00:00:00Z`);
  const to = Date.parse(`${toDate}T00:00:00Z`);
  return Math.floor((to - from) / (1000 * 60 * 60 * 24));
}

export function getResidentialBirAnnualRateDecimal(rateDate = '2026-04-01') {
  return residentialInterestRateFor('BIR', rateDate) / 100;
}

export function getResidentialMpirAnnualRateDecimal(rateDate = '2026-04-01') {
  return residentialInterestRateFor('MPIR', rateDate) / 100;
}

export function calculateResidentialRefundInterest(
  balance: number,
  deadlineDate: string,
  asOfDate?: string,
  refundPeriodStartDate?: string,
) {
  const asOf = asOfDate ?? new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
  const birStart = refundPeriodStartDate ?? addDaysIso(deadlineDate, -13);
  if (asOf <= birStart) return 0;

  const mpirStart = addDaysIso(deadlineDate, 1);
  const birDays = Math.max(0, diffIsoDays(birStart, asOf < mpirStart ? asOf : mpirStart));
  const mpirDays = Math.max(0, diffIsoDays(mpirStart, asOf));
  const birDailyRate = getResidentialBirAnnualRateDecimal(birStart) / 365;
  const mpirDailyRate = getResidentialMpirAnnualRateDecimal(mpirStart) / 365;
  return (
    // eslint-disable-next-line statura/no-floating-point-money -- canonical residential refund interest calc per RAD/DAP rules; final result is rounded to integer cents (Math.round(... * 100) / 100)
    Math.round((balance * birDailyRate * birDays + balance * mpirDailyRate * mpirDays) * 100) / 100
  );
}
